import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { useLocation } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import { useTranslation } from 'react-i18next';
import { useBranding } from '@/hooks/useBranding';
import { Bot } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'nav.dashboard',
  '/devices': 'nav.fleetVehicles',
  '/history': 'nav.history',
  '/reseller': 'nav.overview',
  '/customers': 'nav.customers',
  '/vehicles': 'nav.vehicles',
  '/sim-cards': 'nav.simCards',
  '/contracts': 'nav.contracts',
  '/invoices': 'nav.invoices',
  '/field-services': 'nav.fieldServices',
  '/users': 'nav.users',
  '/geofences': 'nav.geofences',
  '/notifications': 'nav.notifications',
  '/settings': 'nav.settings',
};

const SidebarIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 24 24"
    className="h-4 w-4"
  >
    {collapsed ? (
      <>
        <path
          fill="currentColor"
          d="M19.25 7A2.25 2.25 0 0 0 17 4.75H7A2.25 2.25 0 0 0 4.75 7v10A2.25 2.25 0 0 0 7 19.25h10A2.25 2.25 0 0 0 19.25 17zm1.5 10A3.75 3.75 0 0 1 17 20.75H7A3.75 3.75 0 0 1 3.25 17V7A3.75 3.75 0 0 1 7 3.25h10A3.75 3.75 0 0 1 20.75 7z"
        />
        <path
          fill="currentColor"
          d="M12 7.25a.75.75 0 0 1 .75.75v8a.75.75 0 0 1-.75.75H8a.75.75 0 0 1-.75-.75V8A.75.75 0 0 1 8 7.25z"
        />
      </>
    ) : (
      <path
        fill="currentColor"
        d="M19.25 7A2.25 2.25 0 0 0 17 4.75H7A2.25 2.25 0 0 0 4.75 7v10A2.25 2.25 0 0 0 7 19.25h10A2.25 2.25 0 0 0 19.25 17zm-12 9V8a.75.75 0 0 1 1.5 0v8a.75.75 0 0 1-1.5 0m13.5 1A3.75 3.75 0 0 1 17 20.75H7A3.75 3.75 0 0 1 3.25 17V7A3.75 3.75 0 0 1 7 3.25h10A3.75 3.75 0 0 1 20.75 7z"
      />
    )}
  </svg>
);

interface HeaderProps {
  children?: React.ReactNode;
  headerActions?: React.ReactNode;
  userInitials?: string;
  userEmail?: string;
  onLogout?: () => void;
  onAssistantClick?: () => void;
  isAssistantOpen?: boolean;
  transparent?: boolean;
}

export const Header = ({
  children,
  headerActions,
  userInitials,
  userEmail,
  onLogout,
  onAssistantClick,
  isAssistantOpen,
  transparent = false,
}: HeaderProps) => {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { t } = useTranslation();
  const { title: brandTitle, favicon: brandFavicon } = useBranding();
  const location = useLocation();
  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname];
  const pageTitle = pageTitleKey ? t(pageTitleKey) : '';

  return (
    <header className={transparent ? "absolute inset-x-0 top-0 z-20 px-4 py-3 flex items-center gap-4 bg-gradient-to-b from-black/50 to-transparent text-white" : "bg-card border-b border-border px-4 py-3 flex items-center gap-4"}>
      <div className="flex items-center gap-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 border border-border"
        >
          <SidebarIcon collapsed={isCollapsed} />
        </Button>
        <div className="flex items-center gap-2">
          <img src={brandFavicon || '/favicon.svg'} alt={brandTitle || t('login.title')} className="h-8 w-8 hidden lg:block" />
          <span className="font-semibold text-lg hidden sm:inline">{pageTitle}</span>
        </div>
      </div>
      <div className="@container flex-1 min-w-0 flex items-center gap-1 sm:gap-4 flex-nowrap">
        {children}
      </div>
      <div className="flex items-center gap-1 ml-auto shrink-0">
        {headerActions}
        {onAssistantClick && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isAssistantOpen ? "default" : "ghost"}
                size="icon"
                onClick={onAssistantClick}
                className="h-8 w-8 hidden sm:flex"
              >
                <Bot className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("chat.title")}</p>
            </TooltipContent>
          </Tooltip>
        )}
        <UserAvatar initials={userInitials} email={userEmail} onLogout={onLogout} />
      </div>
    </header>
  );
};
